/**
 * Firestore collection paths — tenant-scoped.
 *
 * Every path is `{namespace}/{tenantId}/{collection}`: a top-level namespace
 * collection, a per-tenant document, and the actual collection beneath it.
 * (Pre-multi-tenancy the middle segment was the literal placeholder '_'.)
 *
 * Values are property getters resolved at ACCESS time against the session
 * tenant (see ./tenant.ts), so the ~300 existing call sites — which read
 * COLLECTIONS.ns.name inside components/hooks after login — keep working
 * unchanged. Do NOT read COLLECTIONS at module scope: before the tenant is
 * resolved the path would be wrong (and will throw after the strictness flip).
 */

import { requireTenantId } from './tenant';

export type Namespace = 'shared' | 'crm' | 'elka' | 'team' | 'quotes' | 'wiki' | 'tasks';

/** `{namespace}/{tenantId}/{collection}` */
export type CollectionPath = `${Namespace}/${string}/${string}`;

function tenantCollections<const K extends readonly string[]>(
  ns: Namespace,
  keys: K
): { readonly [P in K[number]]: CollectionPath } {
  const target = {} as { [P in K[number]]: CollectionPath };
  for (const key of keys) {
    Object.defineProperty(target, key, {
      enumerable: true,
      get: (): CollectionPath => `${ns}/${requireTenantId()}/${key}`,
    });
  }
  return target;
}

export const COLLECTIONS = {
  /**
   * Shared collections accessible by all authenticated users of the tenant
   * (including field workers). LIMITED data only (no sensitive financial info).
   */
  shared: tenantCollections('shared', ['clients', 'projects', 'settings'] as const),

  /** CRM-specific collections */
  crm: tenantCollections('crm', [
    'users',
    'companies',
    'contacts',
    'deals',
    'activities',
    'tasks',
    'tags',
    'dealDocuments',
    'dealComments',
    'pushSubscriptions',
    'notifications',
  ] as const),

  /** Elka Manager-specific collections (office users only) */
  elka: tenantCollections('elka', [
    'clients',
    'projects',
    'orders',
    'workers',
    'suppliers',
    'goodsReceipts',
    'supplies',
    'auditLogs',
    'changeOrders',
    'counters',
    'workSchedule',
    'workAssignments',
    'workDayNotes',
    'supplyItems',
    'projectDocuments',
    'clientDocuments',
    'projectNotes',
    /** Logs of processed inbound supplier emails (Gmail → Cloud Function) */
    'inboundEmails',
    /** Logs of outbound bid-request emails sent to suppliers (Cloud Function → Gmail) */
    'outboundEmails',
    /** Gmail watch lifecycle audit log (setup, renewal, errors) */
    'gmailWatchEvents',
    /** Supplier bid requests (sent to suppliers for pricing) */
    'bidRequests',
    /** Magic-link tokens for supplier self-service price submission */
    'supplierTokens',
    /** Admin-only config docs (gmailWatch state in settings/config) */
    'settings',
    /** Append-only inventory movements ledger — Inventory & Warehouse v1 */
    'inventoryMovements',
    /** Worker-submitted warehouse return drafts (pending → received | rejected) */
    'warehouseReturns',
  ] as const),

  /** Team Portal-specific collections (workers own-data + admin) */
  team: tenantCollections('team', [
    'timeReports',
    'dailyReports',
    'vacationRequests',
    'payslips',
    'notifications',
    'pushSubscriptions',
    'reminderSettings',
    /** Worker-submitted purchase requests (materials needed on site) */
    'purchaseRequests',
    /** Worker petty-cash receipts awaiting reimbursement */
    'pettyCash',
  ] as const),

  /** Quotes app-specific collections (customer-facing price quotes) */
  quotes: tenantCollections('quotes', [
    'quotes',
    'bills',
    'bidRequests',
    'costEstimates',
    'counters',
    'settings',
    'supplierTokens',
    /** AI pricing: canonical historical price entries (materialized view) */
    'priceBook',
    /** AI pricing: job/progress docs for backfill/tag/suggest runs */
    'aiRuns',
    /** AI pricing: cached Dekel catalog snapshot (refreshed human-in-the-loop) */
    'dekelCatalog',
    /** משכ"ל price lists: parsed catalog docs quotes can pull client prices from */
    'priceLists',
    /** Field-level edit history for cost estimates / quotes / bills (written by functions) */
    'auditLogs',
  ] as const),

  /** Wiki app-specific collections */
  wiki: tenantCollections('wiki', ['pages'] as const),

  /** Tasks app-specific collections (projects = epics, tasks = tickets, their files and comments) */
  tasks: tenantCollections('tasks', [
    'epics',
    'tickets',
    'ticketDocuments',
    'ticketComments',
    'epicDocuments',
  ] as const),
};

/**
 * Split a collection path string into an array of path segments.
 * Use this with Firestore's collection() function for nested collections.
 *
 * Example:
 *   collection(db, ...splitPath(COLLECTIONS.shared.clients))
 *   // equivalent to: collection(db, 'shared', '<tenantId>', 'clients')
 */
export function splitPath(path: string): [string, ...string[]] {
  const segments = path.split('/');
  return segments as [string, ...string[]];
}
